/**
 * Chutt... V2 - Comprendre Page (Dark Luxury Theme)
 * Strategic predictive dashboard:
 * S1: Anticipation & Alertes (ExpertCommunication diagnostic)
 * S2: Analyse par Theme (grouped mentions with AI analysis + mission filter)
 * S3: Synthese & Recommandations (diagnostic IA + insights)
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Chip,
  Alert,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShieldIcon from '@mui/icons-material/Shield';
import CampaignIcon from '@mui/icons-material/Campaign';
import HandshakeIcon from '@mui/icons-material/Handshake';
import GroupsIcon from '@mui/icons-material/Groups';
import SpeedIcon from '@mui/icons-material/Speed';

import { motion, AnimatePresence } from 'framer-motion';
import Snackbar from '@mui/material/Snackbar';
import MentionCard from '../components/mentions/MentionCard';
import ScrollReveal from '../components/shared/ScrollReveal';
import CollapsibleSection from '../components/shared/CollapsibleSection';
import LastScanLabel from '../components/shared/LastScanLabel';
import { comprendreAPI } from '../services/apiV2';
import { objectivesAPI, missionsAPI, monitoringAPI, seoAPI } from '../services/api';
import { agirAPI } from '../services/apiV2';
import { invalidateCache } from '../services/cache';
import { colors } from '../styles/colors';
import { getDailyCitation } from '../utils/dailyCitation';

/* ── Dark-theme palette tokens ── */
const dk = {
  bg: colors.charcoal,
  text: colors.ivory,
  textMuted: 'rgba(250,247,242,0.5)',
  textSecondary: 'rgba(250,247,242,0.7)',
  surface: 'rgba(255,255,255,0.06)',
  surfaceHover: 'rgba(255,255,255,0.09)',
  border: 'rgba(255,255,255,0.08)',
  borderMed: 'rgba(255,255,255,0.1)',
  borderStrong: 'rgba(255,255,255,0.15)',
  accent: colors.primaryBright,   // #34D399
  accentDim: 'rgba(52,211,153,0.15)',
};

const ALERT_COLORS = {
  vert: { bg: 'rgba(34,197,94,0.08)', border: colors.success, icon: CheckCircleOutlineIcon, label: 'Situation maitrisee' },
  orange: { bg: 'rgba(245,158,11,0.08)', border: colors.warning, icon: WarningAmberIcon, label: 'Vigilance requise' },
  rouge: { bg: 'rgba(239,68,68,0.08)', border: colors.danger, icon: ErrorOutlineIcon, label: 'Risque critique' },
};

const VELOCITE_ICONS = {
  en_hausse: TrendingUpIcon,
  en_baisse: TrendingDownIcon,
  stable: TrendingFlatIcon,
};

const DIAG_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 heures
const DATA_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 heures

/** Map objective text to mission key */
const OBJ_MISSION_KEYWORDS = {
  protect: ['protect', 'negative_reduction', 'serp', 'seo', 'response_time', 'crise', 'negatif', 'risque'],
  visibility: ['visibility', 'mentions_volume', 'media_coverage', 'source_diversification', 'rayonner', 'visibilit', 'volume', 'couverture'],
  trust: ['trust', 'nss', 'positive_ratio', 'confiance', 'sentiment', 'positif'],
  community: ['community', 'engagement', 'influencer', 'federer', 'communaut'],
};

const MISSION_BADGE_INFO = {
  protect: { icon: <ShieldIcon />, label: 'Proteger' },
  visibility: { icon: <CampaignIcon />, label: 'Rayonner' },
  trust: { icon: <HandshakeIcon />, label: 'Confiance' },
  community: { icon: <GroupsIcon />, label: 'Federer' },
};

function detectMissionKey(objectifText) {
  if (!objectifText) return null;
  const lower = objectifText.toLowerCase();
  for (const [key, keywords] of Object.entries(OBJ_MISSION_KEYWORDS)) {
    if (keywords.some(kw => lower.includes(kw))) return key;
  }
  return null;
}

function getMissionScoreColor(score) {
  if (score >= 70) return colors.success;
  if (score >= 40) return colors.warning;
  return colors.danger;
}

/** Reusable theme accordion item — used in both flat and grouped views */
function ThemeAccordionItem({ theme, index, brands, selectedBrand, renderMissionBadge, handleCreateAction }) {
  const sentColor = theme.sentiment === 'positif' ? colors.success :
    theme.sentiment === 'negatif' ? colors.danger : dk.textMuted;
  const VelociteIcon = VELOCITE_ICONS[theme.velocite] || TrendingFlatIcon;

  return (
    <motion.div
      key={theme.name || index}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <Accordion
        elevation={0}
        sx={{
          mb: 1.5,
          border: `1px solid ${dk.borderMed}`,
          borderRadius: '12px !important',
          overflow: 'hidden',
          bgcolor: 'transparent',
          transition: 'border-color 0.2s ease, background-color 0.2s ease',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: '0 0 12px 0', bgcolor: dk.surfaceHover, borderColor: dk.borderStrong },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: dk.textMuted }} />}
          sx={{
            px: 2.5,
            '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5, minWidth: 0, overflow: 'hidden' },
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontWeight: 700,
                color: dk.text,
              }}
            >
              {theme.name}
            </Typography>
            <Chip
              label={`${theme.mentionCount} mentions`}
              size="small"
              sx={{ height: 20, fontSize: '0.65rem', bgcolor: dk.surface, color: dk.textSecondary, border: `1px solid ${dk.border}` }}
            />
            <Chip
              label={theme.sentiment}
              size="small"
              sx={{
                height: 20, fontSize: '0.65rem', fontWeight: 600,
                bgcolor: theme.sentiment === 'positif' ? 'rgba(34,197,94,0.15)' :
                  theme.sentiment === 'negatif' ? 'rgba(239,68,68,0.15)' : dk.surface,
                color: sentColor,
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <VelociteIcon sx={{ fontSize: 14, color: theme.velocite === 'en_hausse' ? colors.danger : theme.velocite === 'en_baisse' ? colors.success : dk.textMuted }} />
              <Typography variant="caption" sx={{ color: dk.textMuted, fontSize: '0.65rem' }}>
                {theme.velocite === 'en_hausse' ? 'En hausse' : theme.velocite === 'en_baisse' ? 'En baisse' : 'Stable'}
              </Typography>
            </Box>
            {theme.objectifsImpactes?.map((obj, j) => (
              <React.Fragment key={j}>{renderMissionBadge(obj) || <Chip label={obj} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.68rem', borderColor: dk.accent, color: dk.accent }} />}</React.Fragment>
            ))}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2, overflow: 'hidden' }}>
          {/* AI theme analysis */}
          {theme.analyse && (
            <Box sx={{ p: 2.5, mb: 2, bgcolor: dk.accentDim, borderRadius: '12px', borderLeft: `3px solid ${dk.accent}`, border: `1px solid ${dk.accentDim}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <AutoAwesomeIcon sx={{ fontSize: 14, color: dk.accent }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: dk.accent }}>
                  Analyse IA
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: dk.textSecondary, lineHeight: 1.6, wordBreak: 'break-word', textAlign: 'justify' }}>
                {theme.analyse}
              </Typography>
            </Box>
          )}

          {/* Action suggeree */}
          {theme.actionSuggeree && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              {theme.objectifsImpactes?.[0] && renderMissionBadge(theme.objectifsImpactes[0])}
              <Typography variant="body2" sx={{ color: dk.textMuted, flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                Action suggeree : <strong style={{ color: dk.text }}>{theme.actionSuggeree}</strong>
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => handleCreateAction(theme.actionSuggeree, theme.objectifsImpactes?.[0])}
                sx={{ textTransform: 'none', fontSize: '0.72rem', borderRadius: '100px', borderColor: dk.accent, color: dk.accent, flexShrink: 0, px: 2, transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease', '&:hover': { bgcolor: dk.accentDim, borderColor: dk.text } }}
              >
                Creer action
              </Button>
            </Box>
          )}

          {/* Keywords */}
          {theme.keywords?.length > 0 && (
            <Box sx={{ mb: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {theme.keywords.slice(0, 8).map((kw, j) => (
                <Chip key={j} label={kw} size="small" variant="outlined" sx={{ fontSize: '0.68rem', height: 20, borderColor: dk.borderMed, color: dk.textSecondary }} />
              ))}
            </Box>
          )}

          {/* Mentions */}
          {theme.mentions.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {theme.mentions.slice(0, 5).map((m) => (
                <MentionCard
                  key={m.id}
                  mention={m}
                  brandKeywords={brands?.find(b => b.id === selectedBrand)?.keywords || []}
                  compact
                  dark
                />
              ))}
              {theme.mentions.length > 5 && (
                <Typography variant="caption" sx={{ textAlign: 'center', color: dk.textMuted }}>
                  +{theme.mentions.length - 5} autres mentions
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ py: 2, textAlign: 'center', color: dk.textMuted }}>
              Aucune mention directement associee a ce theme.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </motion.div>
  );
}

function ComprendrePage() {
  const { selectedBrand, brands, cacheVersion, lastUpdated } = useOutletContext();
  const navigate = useNavigate();

  // Diagnostic from ExpertCommunication
  const [diagnostic, setDiagnostic] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  // Objectives (kept for cache, not displayed)
  const [, setObjectives] = useState([]);

  // Missions
  const [missions, setMissions] = useState([]);
  const [, setMissionsLoading] = useState(false);
  const [selectedMissionFilter, setSelectedMissionFilter] = useState(null);

  // Comprendre sub-tabs: 0 = DIAGNOSTIC, 1 = BENCHMARK
  const [comprendreTab, setComprendreTab] = useState(0);
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [benchmarkLoading, setBenchmarkLoading] = useState(false);

  // Exclusive accordion: only one section open at a time (null = all closed)
  const [openSection, setOpenSection] = useState(null);

  // Drivers & mentions
  const [drivers, setDrivers] = useState({ positive_drivers: [], negative_drivers: [] });
  const [mentions, setMentions] = useState([]);
  const [loading, setLoading] = useState(true);
  const loadedOnce = useRef(false);
  const diagLoadedOnce = useRef(false);
  const [error, setError] = useState(null);

  // Existing actions (for "update action" feature)
  const [, setExistingActions] = useState([]);

  // Change detection
  const [newMentions, setNewMentions] = useState([]);
  const lastVisitKey = `lastVisit_${selectedBrand}_comprendre`;

  // Snackbar for action creation feedback
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // Track applied preventive actions { index: actionItemId }
  const [appliedActions, setAppliedActions] = useState({});

  const brandName = brands?.find(b => b.id === selectedBrand)?.name || '';
  const [brandImageUrl, setBrandImageUrl] = useState(null);

  /** Render inline mission badge for an objective text */
  const renderMissionBadge = useCallback((objectifText) => {
    const mKey = detectMissionKey(objectifText);
    if (!mKey) return null;
    const info = MISSION_BADGE_INFO[mKey];
    if (!info) return null;
    const m = missions.find(mi => mi.mission_key === mKey);
    const scoreColor = m ? getMissionScoreColor(m.score || 0) : dk.accent;
    return (
      <Chip
        icon={React.cloneElement(info.icon, { sx: { fontSize: '14px !important', color: `${scoreColor} !important` } })}
        label={info.label}
        size="small"
        sx={{
          height: 24, fontSize: '0.68rem', fontWeight: 600,
          bgcolor: `${scoreColor}12`,
          color: scoreColor,
          borderRadius: '100px',
          fontFamily: '"DM Sans", sans-serif',
        }}
      />
    );
  }, [missions]);

  // Data cache key
  const dataCacheKey = `comprendre_data_${selectedBrand}`;

  // Fetch all data (with 24h localStorage cache)
  const fetchData = useCallback(async (force = false) => {
    if (!selectedBrand) return;

    // Check localStorage cache first (unless forced)
    if (!force) {
      try {
        const cached = localStorage.getItem(dataCacheKey);
        if (cached) {
          const { drivers: cDrivers, mentions: cMentions, objectives: cObjectives, actions: cActions, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < DATA_CACHE_TTL) {
            setDrivers(cDrivers || { positive_drivers: [], negative_drivers: [] });
            setMentions(cMentions || []);
            setObjectives(cObjectives || []);
            setExistingActions(cActions || []);
            // Detect new mentions since last visit
            const lastVisit = localStorage.getItem(lastVisitKey);
            if (lastVisit) {
              const lastVisitDate = new Date(lastVisit);
              const newOnes = (cMentions || []).filter(m =>
                m.published_at && new Date(m.published_at) > lastVisitDate
              );
              setNewMentions(newOnes);
            }
            setLoading(false);
            loadedOnce.current = true;
            return;
          }
        }
      } catch (_) { /* ignore parse errors */ }
    }

    if (!loadedOnce.current) setLoading(true);
    setError(null);

    try {
      const [driversRes, mentionsRes, objectivesRes, actionsRes] = await Promise.allSettled([
        comprendreAPI.getDrivers(selectedBrand, null, force),
        comprendreAPI.getMentions(selectedBrand, { limit: 50, days: 30 }, force),
        objectivesAPI.getProgress(selectedBrand),
        agirAPI.getActionPlan(selectedBrand),
      ]);

      const driversData = driversRes.status === 'fulfilled' ? (driversRes.value?.data || {}) : {};
      const mentionsData = mentionsRes.status === 'fulfilled' ? (mentionsRes.value?.data?.mentions || []) : [];
      const objectivesData = objectivesRes.status === 'fulfilled' ? (objectivesRes.value?.data?.objectives || []) : [];
      const actionsData = actionsRes.status === 'fulfilled' ? (actionsRes.value?.data?.actions || []) : [];

      setDrivers(driversData);
      setMentions(mentionsData);
      setObjectives(objectivesData);
      setExistingActions(actionsData);

      // Detect new mentions since last visit
      const lastVisit = localStorage.getItem(lastVisitKey);
      if (lastVisit) {
        const lastVisitDate = new Date(lastVisit);
        const newOnes = mentionsData.filter(m =>
          m.published_at && new Date(m.published_at) > lastVisitDate
        );
        setNewMentions(newOnes);
      }

      // Save to localStorage cache
      try {
        localStorage.setItem(dataCacheKey, JSON.stringify({
          drivers: driversData,
          mentions: mentionsData,
          objectives: objectivesData,
          actions: actionsData,
          timestamp: Date.now(),
        }));
      } catch (_) { /* localStorage full — ignore */ }
    } catch (err) {
      console.error('Comprendre fetch error:', err);
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
      loadedOnce.current = true;
    }
  }, [selectedBrand, lastVisitKey, dataCacheKey]);

  // Fetch diagnostic separately (slow, AI) — with localStorage cache to avoid reload on every navigation
  const diagCacheKey = `diag_cache_${selectedBrand}`;

  const fetchDiagnostic = useCallback(async (force = false) => {
    if (!selectedBrand) return;

    // Check localStorage cache first (unless forced)
    if (!force) {
      try {
        const cached = localStorage.getItem(diagCacheKey);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < DIAG_CACHE_TTL) {
            setDiagnostic(data);
            diagLoadedOnce.current = true;
            return;
          }
        }
      } catch (_) { /* ignore parse errors */ }
    }

    if (!diagLoadedOnce.current) setDiagLoading(true);
    try {
      const res = await comprendreAPI.diagnose(selectedBrand, force);
      const diagData = res.data?.diagnostic || null;
      setDiagnostic(diagData);
      // Cache in localStorage
      if (diagData) {
        localStorage.setItem(diagCacheKey, JSON.stringify({ data: diagData, timestamp: Date.now() }));
      }
    } catch (err) {
      console.error('Diagnostic error:', err);
    } finally {
      setDiagLoading(false);
      diagLoadedOnce.current = true;
    }
  }, [selectedBrand, diagCacheKey]);

  // Fetch missions — no spinner if already loaded (cache serves fast)
  const fetchMissions = useCallback(async () => {
    if (!selectedBrand) return;
    const showSpinner = missions.length === 0;
    if (showSpinner) setMissionsLoading(true);
    try {
      const res = await missionsAPI.getProgress(selectedBrand);
      const data = res.data || res;
      setMissions(data.missions || []);
    } catch (err) {
      console.warn('Missions fetch warning:', err);
    } finally {
      if (showSpinner) setMissionsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand]);

  // Fetch brand image (use photo_urls[1] = slot 2 for Comprendre)
  const fetchBrandImage = useCallback(async () => {
    if (!selectedBrand) return;
    try {
      const res = await monitoringAPI.getBrandImages(selectedBrand);
      const data = res.data || {};
      const urls = data.photo_urls || [];
      if (urls.length > 1) {
        setBrandImageUrl(urls[1]);
      } else if (urls.length > 0) {
        setBrandImageUrl(urls[0]);
      } else if (data.brand_photo_url) {
        setBrandImageUrl(data.brand_photo_url);
      } else {
        const images = data.images || [];
        if (images.length > 0) {
          setBrandImageUrl(images[0].image_url || images[0].url || null);
        }
      }
    } catch (err) {
      // Silent fail — brand image is optional
    }
  }, [selectedBrand]);

  // Fetch benchmark data for BENCHMARK tab — no spinner if already loaded
  const fetchBenchmark = useCallback(async () => {
    if (!selectedBrand) return;
    const showSpinner = !benchmarkData;
    if (showSpinner) setBenchmarkLoading(true);
    try {
      const res = await monitoringAPI.getBenchmark(selectedBrand);
      const data = res.data || res;
      setBenchmarkData(data);
    } catch (e) { console.warn('[benchmark] fetch error', e); }
    if (showSpinner) setBenchmarkLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchDiagnostic(); }, [fetchDiagnostic]);
  useEffect(() => { fetchMissions(); }, [fetchMissions]);
  useEffect(() => { fetchBrandImage(); }, [fetchBrandImage]);

  // Re-fetch when CRON freshness invalidates caches (only on actual change, not on mount)
  const prevCacheVersion = useRef(cacheVersion);
  useEffect(() => {
    if (cacheVersion !== prevCacheVersion.current) {
      prevCacheVersion.current = cacheVersion;
      fetchData();
      fetchDiagnostic();
    }
  }, [cacheVersion]); // eslint-disable-line react-hooks/exhaustive-deps


  // Update last visit timestamp on unmount
  useEffect(() => {
    return () => {
      if (selectedBrand) {
        localStorage.setItem(lastVisitKey, new Date().toISOString());
      }
    };
  }, [selectedBrand, lastVisitKey]);

  // Group mentions by theme (driver name), with mission_key support
  const themeGroups = useMemo(() => {
    const allDrivers = [
      ...(drivers.positive_drivers || []),
      ...(drivers.negative_drivers || []),
    ];

    // Enrich with diagnostic themes_cles
    const diagThemes = diagnostic?.themes_cles || [];

    const groups = allDrivers.map(d => {
      const name = (d.name || d.topic_name || '').toLowerCase();
      const keywords = (d.keywords || []).map(k => k.toLowerCase());

      // Find matching mentions
      const matching = mentions.filter(m => {
        if (!m.text) return false;
        const text = m.text.toLowerCase();
        return text.includes(name) || keywords.some(kw => text.includes(kw));
      });

      // Find matching diagnostic theme
      const diagTheme = diagThemes.find(t =>
        (t.theme || '').toLowerCase() === name ||
        (t.theme || '').toLowerCase().includes(name)
      );

      return {
        name: d.name || d.topic_name,
        missionKey: d.mission_key || null,
        sentiment: d.topic_type?.includes('positive') ? 'positif' : d.topic_type?.includes('negative') ? 'negatif' : 'neutre',
        mentionCount: matching.length > 0 ? matching.length : (d.metrics?.mention_count || d.mention_count || d.mentions || 0),
        impactScore: d.metrics?.impact_score || d.impact_score || d.score || 0,
        keywords: d.keywords || [],
        mentions: matching.slice(0, 10),
        velocite: diagTheme?.velocite || 'stable',
        analyse: diagTheme?.analyse || null,
        objectifsImpactes: diagTheme?.objectifs_impactes || [],
        actionSuggeree: diagTheme?.action_suggeree || null,
      };
    });

    // Filter out themes with 0 mentions (no real data)
    const filtered = groups.filter(g => g.mentionCount > 0 || g.mentions.length > 0);

    // Sort by impact
    filtered.sort((a, b) => b.impactScore - a.impactScore);

    // Filter by selected mission (using mission_key from driver)
    if (selectedMissionFilter) {
      return filtered.filter(g => g.missionKey === selectedMissionFilter);
    }

    return filtered;
  }, [drivers, mentions, diagnostic, selectedMissionFilter]);

  // Handle "Creer action" from insight — creates ActionItem (visible in Agir Plan d'Action)
  // extraContext: { impact_predit, plateforme, delai } from preventive actions
  // preventiveIndex: if set, track this action in appliedActions for gray-out
  const handleCreateAction = async (actionText, objectifLie, extraContext, preventiveIndex) => {
    try {
      // Calculate priority from delai if available
      let priority = 'medium';
      const delai = extraContext?.delai;
      if (delai === '24h') priority = 'urgent';
      else if (delai === '48h') priority = 'high';

      // Build enriched description
      let description = actionText;
      if (extraContext?.impact_predit) {
        description += `\n\nImpact predit: ${extraContext.impact_predit}`;
      }

      // Resolve mission_key: from AI field first, then from objectif label
      const resolvedMissionKey = extraContext?.mission_key || detectMissionKey(objectifLie) || null;

      // Map action_type from AI to category
      const ACTION_TYPE_TO_CATEGORY = {
        'creer_contenu': 'communicate',
        'effacer_contenu': 'escalate',
        'seo_serp': 'monitor',
        'repondre_mentions': 'respond',
        'relations_presse': 'communicate',
        'planifier_publication': 'communicate',
      };
      const category = ACTION_TYPE_TO_CATEGORY[extraContext?.action_type] || 'communicate';

      const res = await agirAPI.createActionItem(selectedBrand, {
        title: actionText,
        description,
        category,
        priority,
        mission_key: resolvedMissionKey,
        action_type: extraContext?.action_type || null,
        platform: extraContext?.plateforme || null,
        objective_type: objectifLie || null,
        source_type: 'ai_diagnostic',
        impact_predit: extraContext?.impact_predit || null,
        delai: delai || null,
      });
      // Track applied preventive action (fallback true if API returns no ID)
      const newActionId = res.data?.id || res.data?.action_id || true;
      if (preventiveIndex != null) {
        setAppliedActions(prev => ({ ...prev, [preventiveIndex]: newActionId }));
      }
      // Invalidate action plan cache so Agir sees the new action immediately
      invalidateCache(`v2/agir/plan/${selectedBrand}`);
      const actionsRes = await agirAPI.getActionPlan(selectedBrand);
      setExistingActions(actionsRes.data?.actions || []);
      setSnackbar({ open: true, message: 'Action ajoutee au Plan d\'Action (Agir)' });
    } catch (err) {
      console.error('Create action error:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la creation de l\'action' });
    }
  };

  // Cancel a previously applied preventive action
  const handleCancelAction = async (index) => {
    const actionId = appliedActions[index];
    if (actionId && actionId !== true) {
      try {
        await agirAPI.deleteActionItem(actionId);
        invalidateCache(`v2/agir/plan/${selectedBrand}`);
        const res = await agirAPI.getActionPlan(selectedBrand);
        setExistingActions(res.data?.actions || []);
      } catch (_) { /* silent */ }
    }
    setAppliedActions(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  // Mission labels and icons for section headers & chips
  const MISSION_SECTIONS = useMemo(() => ({
    protect: { label: 'Proteger', Icon: ShieldIcon },
    visibility: { label: 'Visibilite', Icon: CampaignIcon },
    trust: { label: 'Confiance', Icon: HandshakeIcon },
    community: { label: 'Communaute', Icon: GroupsIcon },
  }), []);

  // Group themeGroups by mission for section display (when no filter selected)
  const groupedByMission = useMemo(() => {
    if (selectedMissionFilter) return null; // Not needed when filtering
    const missionKeys = ['protect', 'visibility', 'trust', 'community'];
    const groups = {};
    for (const mk of missionKeys) {
      const items = themeGroups.filter(t => t.missionKey === mk);
      if (items.length > 0) {
        groups[mk] = items;
      }
    }
    // Drivers without a mission_key
    const unclassified = themeGroups.filter(t => !t.missionKey);
    if (unclassified.length > 0) {
      groups._unclassified = unclassified;
    }
    return groups;
  }, [themeGroups, selectedMissionFilter]);

  // Available mission keys (only those that have at least one driver)
  const availableMissionKeys = useMemo(() => {
    const allDrivers = [
      ...(drivers.positive_drivers || []),
      ...(drivers.negative_drivers || []),
    ];
    const keys = new Set();
    for (const d of allDrivers) {
      if (d.mission_key) keys.add(d.mission_key);
    }
    return ['protect', 'visibility', 'trust', 'community'].filter(k => keys.has(k));
  }, [drivers]);

  // Dynamic border color for Analyse par Theme — based on dominant sentiment
  const themeBorderColor = useMemo(() => {
    if (themeGroups.length === 0) return colors.warning;
    const pos = themeGroups.filter(t => t.sentiment === 'positif').length;
    const neg = themeGroups.filter(t => t.sentiment === 'negatif').length;
    if (pos > neg) return colors.success;
    if (neg > pos) return colors.danger;
    return colors.warning;
  }, [themeGroups]);

  // Summary for themes section
  const themeSummary = useMemo(() => {
    if (themeGroups.length === 0) return 'Aucun theme identifie. Lancez un calcul mensuel.';
    const pos = themeGroups.filter(t => t.sentiment === 'positif').length;
    const neg = themeGroups.filter(t => t.sentiment === 'negatif').length;
    const pct = Math.round((pos / themeGroups.length) * 100);
    const rising = themeGroups.filter(t => t.velocite === 'en_hausse').length;
    let summary = `${themeGroups.length} themes identifies. Sentiment majoritairement ${pct >= 50 ? 'positif' : 'negatif'} (${pct}%).`;
    if (neg > 0) summary += ` ${neg} sujet${neg > 1 ? 's' : ''} negatif${neg > 1 ? 's' : ''} a surveiller`;
    if (rising > 0) summary += `, ${rising} en acceleration`;
    if (neg > 0 || rising > 0) summary += '.';
    return summary;
  }, [themeGroups]);

  /* ── No brand selected ── */
  if (!selectedBrand) {
    return (
      <Box sx={{ bgcolor: dk.bg, minHeight: '100vh', mx: { xs: -2, sm: -3, md: -7.5 }, mt: -3, px: 3, pt: 3, pb: 4 }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: dk.textMuted }}>
            Selectionnez une marque pour comprendre sa reputation
          </Typography>
        </Box>
      </Box>
    );
  }

  const alertConfig = ALERT_COLORS[diagnostic?.niveau_alerte] || ALERT_COLORS.vert;
  const AlertIcon = alertConfig.icon;

  return (
    <Box sx={{ bgcolor: dk.bg, minHeight: '100vh', mx: { xs: -2, sm: -3, md: -7.5 }, mt: -3, px: { xs: 2, sm: 4, md: 5 }, pt: { xs: 3, sm: 5 }, pb: 6, overflowX: 'hidden' }}>

      {/* ── Header (matches Radar pattern) ── */}
      <ScrollReveal>
        <Box sx={{ pt: 5, pb: 4, mb: 4, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontWeight: 400,
                fontSize: { xs: '3.2rem', sm: '4.8rem' },
                color: dk.text,
                lineHeight: 1.1,
                mb: 1,
                textWrap: 'balance',
              }}
            >
              Ce qui{' '}
              <Box component="em" sx={{ color: dk.accent, fontStyle: 'italic' }}>
                façonne
              </Box>
              {' '}votre reputation
            </Typography>
            <Typography
              sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '1rem',
                color: dk.textMuted,
                fontWeight: 400,
              }}
            >
              {brandName} — Diagnostic strategique predictif.
            </Typography>
            <LastScanLabel timestamp={lastUpdated} dark />
          </Box>
          <Button
            variant="outlined"
            startIcon={diagLoading ? <CircularProgress size={14} sx={{ color: dk.accent }} /> : <RefreshIcon />}
            disabled={diagLoading}
            onClick={() => {
              fetchData(true); fetchDiagnostic(true);
              // Also refresh SERP + invalidate Expert RP cache (Agir will read fresh on next visit)
              if (selectedBrand) {
                seoAPI.scanSerp(selectedBrand, null, true).catch(() => {});
                invalidateCache(`seo/expert-rp/${selectedBrand}`);
              }
            }}
            sx={{
              borderColor: dk.accent,
              color: dk.accent,
              borderRadius: '100px',
              textTransform: 'none',
              fontFamily: '"DM Sans", sans-serif',
              fontWeight: 600,
              fontSize: '0.78rem',
              flexShrink: 0,
              alignSelf: 'center',
              px: 2.5,
              transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
              '&:hover': { borderColor: dk.text, bgcolor: 'rgba(52,211,153,0.08)' },
            }}
          >
            Actualiser
          </Button>
        </Box>
      </ScrollReveal>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 3 }}>
          <CircularProgress size={36} sx={{ color: dk.accent }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontFamily: '"Playfair Display", Georgia, serif', color: dk.text, mb: 0.5 }}>
              Analyse en cours...
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: '"DM Sans", sans-serif', color: dk.textMuted, maxWidth: 360 }}>
              L'Expert IA analyse vos mentions et construit le diagnostic. Premiere generation : environ 30 secondes.
            </Typography>
          </Box>
          <Box sx={{ width: '100%', maxWidth: 400 }}>
            <LinearProgress sx={{ borderRadius: 1, bgcolor: dk.border, '& .MuiLinearProgress-bar': { bgcolor: dk.accent } }} />
          </Box>
        </Box>
      ) : !diagnostic && themeGroups.length === 0 && mentions.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" sx={{ fontFamily: "'DM Sans', sans-serif", color: dk.textMuted, mb: 1, textWrap: 'balance' }}>
            Aucun driver identifie
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: "'DM Sans', sans-serif", color: dk.textSecondary, mb: 3, textWrap: 'pretty' }}>
            Lancez une collecte puis un diagnostic pour analyser les thematiques.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/radar')}
            sx={{
              borderColor: dk.accent,
              color: dk.accent,
              borderRadius: '100px',
              textTransform: 'none',
              fontFamily: '"DM Sans", sans-serif',
              fontWeight: 600,
              '&:hover': { bgcolor: dk.accentDim },
            }}
          >
            Aller au Radar
          </Button>
        </Box>
      ) : (
        <>
          {/* Split layout: Sections LEFT | Photo RIGHT — always 2 columns on md+ */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 3, sm: 4, md: 5 } }}>
            {/* LEFT — Accordion Sections + Benchmark */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Tab bar: DIAGNOSTIC · BENCHMARK */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
              {['DIAGNOSTIC', 'BENCHMARK'].map((label, idx) => (
                <Typography
                  key={label}
                  onClick={() => {
                    setComprendreTab(idx);
                    if (idx === 1 && !benchmarkData) fetchBenchmark();
                  }}
                  sx={{
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: comprendreTab === idx ? dk.text : dk.textMuted,
                    cursor: 'pointer',
                    pb: 0.5,
                    borderBottom: comprendreTab === idx ? `2px solid ${dk.accent}` : '2px solid transparent',
                    transition: 'background-color 0.25s ease, color 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease',
                    '&:hover': { color: dk.text },
                    userSelect: 'none',
                  }}
                >
                  {label}
                </Typography>
              ))}
            </Box>

          {comprendreTab === 0 && (<>
          {/* ======= SECTION 1: Anticipation & Alertes ======= */}
          <ScrollReveal>
              <CollapsibleSection
                dark
                title="Anticipation & Alertes"
                summary={diagnostic
                  ? (() => {
                      const sigCount = diagnostic.anticipation?.signaux_faibles?.length || 0;
                      const niveau = diagnostic.niveau_alerte;
                      if (niveau === 'rouge') return `Risque critique — ${sigCount} signaux detectes. Action immediate recommandee.`;
                      if (niveau === 'orange') return `Vigilance requise — ${sigCount} signaux faibles. Surveillance renforcee conseillee.`;
                      return `Situation maitrisee — ${sigCount} signaux faibles detectes. Aucun risque critique a court terme.`;
                    })()
                  : 'Cliquez Actualiser pour lancer le diagnostic IA'}
                borderColor={alertConfig.border}
                info="Diagnostic predictif par l'IA. Analyse les signaux faibles et les scenarios de risque pour anticiper les crises avant qu'elles n'eclatent."
                isOpen={openSection === 'anticipation'}
                onToggle={() => setOpenSection(openSection === 'anticipation' ? null : 'anticipation')}
                sx={{ mb: 0 }}
              >

                {diagLoading && !diagnostic ? (
                  <Box sx={{ py: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: dk.textMuted }}>
                      Analyse en cours par l'Expert Communication IA...
                    </Typography>
                    <LinearProgress sx={{ borderRadius: 1, bgcolor: dk.border, '& .MuiLinearProgress-bar': { bgcolor: dk.accent } }} />
                  </Box>
                ) : diagnostic ? (
                  <>
                    {/* Alert level banner */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <AlertIcon sx={{ color: alertConfig.border }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: alertConfig.border }}>
                        {alertConfig.label}
                      </Typography>
                      {diagnostic.anticipation?.risque_score > 0 && (
                        <Chip
                          icon={<SpeedIcon sx={{ fontSize: '14px !important', color: 'inherit' }} />}
                          label={`Risque: ${diagnostic.anticipation.risque_score}/100`}
                          size="small"
                          sx={{
                            bgcolor: diagnostic.anticipation.risque_score > 60 ? 'rgba(239,68,68,0.15)' :
                              diagnostic.anticipation.risque_score > 30 ? 'rgba(245,158,11,0.15)' : 'rgba(34,197,94,0.15)',
                            color: dk.text,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        />
                      )}
                    </Box>

                    {/* Prediction text */}
                    {diagnostic.anticipation?.prediction && (
                      <Typography variant="body1" sx={{ color: dk.textSecondary, mb: 3, fontWeight: 500, lineHeight: 1.7, wordBreak: 'break-word', textAlign: 'justify' }}>
                        {diagnostic.anticipation.prediction}
                      </Typography>
                    )}

                    {/* Signaux faibles */}
                    {diagnostic.anticipation?.signaux_faibles?.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: dk.text }}>
                          Signaux faibles detectes
                        </Typography>
                        {diagnostic.anticipation.signaux_faibles.map((s, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                            <Chip
                              label={s.urgence}
                              size="small"
                              sx={{
                                height: 20, fontSize: '0.65rem', fontWeight: 600,
                                bgcolor: s.urgence === 'haute' ? 'rgba(239,68,68,0.15)' : s.urgence === 'moyenne' ? 'rgba(245,158,11,0.15)' : dk.surface,
                                color: s.urgence === 'haute' ? colors.danger : s.urgence === 'moyenne' ? colors.warning : dk.textMuted,
                              }}
                            />
                            <Typography variant="body2" sx={{ color: dk.textSecondary, wordBreak: 'break-word', minWidth: 0, textAlign: 'justify' }}>
                              {s.signal}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Actions preventives */}
                    {diagnostic.anticipation?.actions_preventives?.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: dk.text }}>
                          Actions preventives recommandees
                        </Typography>
                        {diagnostic.anticipation.actions_preventives.map((a, i) => {
                          const isApplied = appliedActions[i] != null;
                          return (
                            <Box key={i} sx={{
                              p: 2, mb: 1, bgcolor: dk.surface, borderRadius: '12px',
                              border: `1px solid ${dk.border}`, overflow: 'hidden',
                              opacity: isApplied ? 0.45 : 1,
                              transition: 'opacity 0.3s ease',
                            }}>
                              <Box sx={{ pointerEvents: isApplied ? 'none' : 'auto' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: dk.text, wordBreak: 'break-word' }}>
                                  {a.action}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1, alignItems: 'center' }}>
                                  {a.action_type && <Chip label={({'creer_contenu':'Creer contenu','effacer_contenu':'Effacer contenu','seo_serp':'SEO/SERP','repondre_mentions':'Repondre','relations_presse':'Relations','planifier_publication':'Planifier'})[a.action_type] || a.action_type} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(59,130,246,0.12)', color: '#3B82F6', fontWeight: 600 }} />}
                                  {a.plateforme && <Chip label={a.plateforme} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: dk.accentDim, color: dk.accent }} />}
                                  {a.delai && <Chip label={`Delai: ${a.delai}`} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: dk.surface, color: dk.textSecondary, border: `1px solid ${dk.border}` }} />}
                                  {(a.mission_key || a.objectif_lie) && renderMissionBadge(a.mission_key || a.objectif_lie)}
                                </Box>
                                {a.impact_predit && (
                                  <Typography variant="caption" sx={{ color: dk.textMuted, fontStyle: 'italic', wordBreak: 'break-word', display: 'block' }}>
                                    Impact predit : {a.impact_predit}
                                  </Typography>
                                )}
                              </Box>
                              <Box sx={{ mt: 1 }}>
                                {isApplied ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleCancelAction(i)}
                                    sx={{
                                      textTransform: 'none', fontSize: '0.78rem', borderRadius: '100px',
                                      borderColor: dk.borderMed, color: dk.textMuted,
                                      opacity: 1, pointerEvents: 'auto',
                                      transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                                      '&:hover': { borderColor: dk.textSecondary, color: dk.textSecondary },
                                    }}
                                  >
                                    Annuler cette action
                                  </Button>
                                ) : (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<AddCircleOutlineIcon />}
                                    onClick={() => handleCreateAction(a.action, a.objectif_lie, {
                                      impact_predit: a.impact_predit,
                                      plateforme: a.plateforme,
                                      delai: a.delai,
                                      action_type: a.action_type || null,
                                      mission_key: a.mission_key || null,
                                    }, i)}
                                    sx={{
                                      textTransform: 'none', fontSize: '0.75rem', borderRadius: '100px',
                                      bgcolor: dk.accent, color: dk.bg, px: 2.5, py: 0.75,
                                      boxShadow: '0 2px 12px rgba(52,211,153,0.25)',
                                      transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                                      '&:hover': { bgcolor: '#2FBF8A', transform: 'translateY(-1px)', boxShadow: '0 4px 16px rgba(52,211,153,0.3)' },
                                    }}
                                  >
                                    Appliquer cette action
                                  </Button>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    )}

                    {/* New since last visit */}
                    {newMentions.length > 0 && (
                      <Box sx={{ p: 2, bgcolor: 'rgba(59,130,246,0.1)', borderRadius: '12px', border: '1px solid rgba(59,130,246,0.3)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <FiberNewIcon sx={{ color: '#60A5FA' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#93C5FD' }}>
                            {newMentions.length} nouvelle{newMentions.length > 1 ? 's' : ''} mention{newMentions.length > 1 ? 's' : ''} depuis votre derniere visite
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleCreateAction(`Traiter ${newMentions.length} nouvelles mentions`, null)}
                            sx={{ textTransform: 'none', fontSize: '0.75rem', borderRadius: '100px', borderColor: '#60A5FA', color: '#93C5FD', transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease', '&:hover': { bgcolor: 'rgba(59,130,246,0.15)', borderColor: '#93C5FD' } }}
                          >
                            Creer action
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ py: 2 }}>
                    <Typography variant="body2" sx={{ color: dk.textMuted }}>
                      Cliquez sur "Actualiser" pour lancer le diagnostic Expert Communication.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={fetchDiagnostic}
                      sx={{ mt: 1, textTransform: 'none', borderRadius: '100px', borderColor: dk.accent, color: dk.accent, transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease', '&:hover': { bgcolor: dk.accentDim, borderColor: dk.text } }}
                    >
                      Lancer le diagnostic
                    </Button>
                  </Box>
                )}
              </CollapsibleSection>
          </ScrollReveal>

          {/* ======= SECTION 2: Analyse par Theme ======= */}
          <ScrollReveal delay={0.1}>
              <CollapsibleSection
                dark
                title={`Analyse par Theme (${themeGroups.length})`}
                summary={themeSummary}
                borderColor={themeBorderColor}
                info="Regroupement thematique de vos mentions. L'IA identifie les sujets dominants, leur sentiment et leur velocite pour comprendre votre image."
                isOpen={openSection === 'themes'}
                onToggle={() => setOpenSection(openSection === 'themes' ? null : 'themes')}
                sx={{ mb: 0 }}
              >
                {/* Mission filter chips */}
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2.5 }}>
                  <Chip
                    label="Tous"
                    size="small"
                    onClick={() => setSelectedMissionFilter(null)}
                    sx={{
                      height: 28, fontSize: '0.72rem', fontWeight: 600,
                      bgcolor: !selectedMissionFilter ? dk.accentDim : dk.surface,
                      color: !selectedMissionFilter ? dk.accent : dk.textSecondary,
                      border: `1px solid ${!selectedMissionFilter ? dk.accent : dk.border}`,
                      transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': { bgcolor: dk.accentDim, borderColor: dk.accent },
                    }}
                  />
                  {['protect', 'visibility', 'trust', 'community'].map((mk) => {
                    const info = MISSION_SECTIONS[mk];
                    if (!info) return null;
                    const isSelected = selectedMissionFilter === mk;
                    const hasDrivers = availableMissionKeys.includes(mk);
                    const MIcon = info.Icon;
                    return (
                      <Chip
                        key={mk}
                        icon={<MIcon sx={{ fontSize: '15px !important', color: isSelected ? `${dk.accent} !important` : hasDrivers ? `${dk.textSecondary} !important` : `${dk.textMuted} !important` }} />}
                        label={info.label}
                        size="small"
                        onClick={() => setSelectedMissionFilter(isSelected ? null : mk)}
                        sx={{
                          height: 28, fontSize: '0.72rem', fontWeight: 600,
                          bgcolor: isSelected ? dk.accentDim : dk.surface,
                          color: isSelected ? dk.accent : hasDrivers ? dk.textSecondary : dk.textMuted,
                          border: `1px solid ${isSelected ? dk.accent : dk.border}`,
                          opacity: hasDrivers || isSelected ? 1 : 0.5,
                          transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': { bgcolor: dk.accentDim, borderColor: dk.accent, opacity: 1 },
                        }}
                      />
                    );
                  })}
                </Box>

                <AnimatePresence>
                  {themeGroups.length > 0 ? (
                    <>
                      {/* When a mission filter is active OR no grouping available, flat list */}
                      {(selectedMissionFilter || !groupedByMission) ? (
                        themeGroups.map((theme, i) => (
                          <ThemeAccordionItem
                            key={theme.name || i}
                            theme={theme}
                            index={i}
                            brands={brands}
                            selectedBrand={selectedBrand}
                            renderMissionBadge={renderMissionBadge}
                            handleCreateAction={handleCreateAction}
                          />
                        ))
                      ) : (
                        /* Grouped by mission with section headers */
                        Object.entries(groupedByMission).map(([mk, items]) => {
                          const missionInfo = MISSION_SECTIONS[mk];
                          const SectionIcon = missionInfo?.Icon || AutoAwesomeIcon;
                          const sectionLabel = missionInfo?.label || 'Autres themes';
                          return (
                            <Box key={mk} sx={{ mb: 3 }}>
                              {/* Mission section header */}
                              <motion.div
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.3 }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 1.5,
                                    pb: 1,
                                    borderBottom: `1px solid ${dk.border}`,
                                  }}
                                >
                                  <SectionIcon sx={{ fontSize: 18, color: dk.accent }} />
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontFamily: '"Playfair Display", Georgia, serif',
                                      fontWeight: 600,
                                      fontSize: '0.95rem',
                                      color: dk.text,
                                    }}
                                  >
                                    {sectionLabel}
                                  </Typography>
                                  <Chip
                                    label={`${items.length} theme${items.length > 1 ? 's' : ''}`}
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.65rem',
                                      fontWeight: 600,
                                      bgcolor: dk.accentDim,
                                      color: dk.accent,
                                    }}
                                  />
                                </Box>
                              </motion.div>
                              {items.map((theme, i) => (
                                <ThemeAccordionItem
                                  key={theme.name || `${mk}-${i}`}
                                  theme={theme}
                                  index={i}
                                  brands={brands}
                                  selectedBrand={selectedBrand}
                                  renderMissionBadge={renderMissionBadge}
                                  handleCreateAction={handleCreateAction}
                                />
                              ))}
                            </Box>
                          );
                        })
                      )}
                    </>
                  ) : (
                    <Alert severity="info" sx={{ bgcolor: dk.surface, color: dk.text, '& .MuiAlert-icon': { color: dk.accent } }}>
                      Aucun theme identifie. Lancez un calcul mensuel ou collectez plus de mentions.
                    </Alert>
                  )}
                </AnimatePresence>
              </CollapsibleSection>
          </ScrollReveal>

          {/* ======= SECTION 3: Synthese & Recommandations ======= */}
          {diagnostic && (
              <ScrollReveal delay={0.2}>
                <CollapsibleSection
                  dark
                  title="Synthese & Recommandations"
                  summary={diagnostic.diagnostic_global
                    ? diagnostic.diagnostic_global.substring(0, 120) + '...'
                    : `${diagnostic.insights?.length || 0} insights cles · ${diagnostic.objectifs_impact?.length || 0} objectifs analyses`}
                  borderColor={colors.warning}
                  info="Synthese globale par l'IA. Combine mentions, themes et objectifs pour des recommandations actionnables et priorisees."
                  isOpen={openSection === 'synthese'}
                  onToggle={() => setOpenSection(openSection === 'synthese' ? null : 'synthese')}
                  sx={{ mb: 0 }}
                >

                  {/* Diagnostic global */}
                  {diagnostic.diagnostic_global && (
                    <Box sx={{ p: 2.5, mb: 2, bgcolor: dk.accentDim, borderRadius: '12px', borderLeft: `3px solid ${dk.accent}`, border: `1px solid ${dk.accentDim}` }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: dk.accent }}>
                        Diagnostic IA
                      </Typography>
                      <Typography variant="body1" sx={{ color: dk.textSecondary, lineHeight: 1.7, wordBreak: 'break-word', textAlign: 'justify' }}>
                        {diagnostic.diagnostic_global}
                      </Typography>
                    </Box>
                  )}

                  {/* Objectives impact */}
                  {diagnostic.objectifs_impact?.length > 0 && (
                    <Box sx={{ mb: 2, overflow: 'hidden' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: dk.text }}>
                        Impact sur vos objectifs
                      </Typography>
                      <Grid container spacing={1.5}>
                        {diagnostic.objectifs_impact.map((obj, i) => (
                          <Grid item xs={12} sm={6} key={i}>
                            <Box sx={{ p: 2, border: `1px solid ${dk.border}`, borderRadius: '12px', bgcolor: dk.surface }}>
                              <Box sx={{ mb: 0.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: dk.text, mb: 0.5 }}>
                                  {obj.objectif_name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  <Chip
                                    label={obj.status === 'atteint' ? 'Atteint' : obj.status === 'en_danger' ? 'En danger' : 'En progres'}
                                    size="small"
                                    sx={{
                                      height: 18, fontSize: '0.68rem', fontWeight: 600,
                                      bgcolor: obj.status === 'atteint' ? 'rgba(34,197,94,0.15)' : obj.status === 'en_danger' ? 'rgba(239,68,68,0.15)' : dk.accentDim,
                                      color: obj.status === 'atteint' ? colors.success : obj.status === 'en_danger' ? colors.danger : dk.accent,
                                    }}
                                  />
                                  {renderMissionBadge(obj.objectif_name)}
                                </Box>
                              </Box>
                              <Typography variant="body2" sx={{ color: dk.textMuted, mb: 0.5, wordBreak: 'break-word', textAlign: 'justify' }}>
                                {obj.analyse}
                              </Typography>
                              {obj.facteurs_positifs?.length > 0 && (
                                <Typography variant="caption" sx={{ color: colors.success, wordBreak: 'break-word', display: 'block' }}>
                                  + {obj.facteurs_positifs.join(', ')}
                                </Typography>
                              )}
                              {obj.facteurs_negatifs?.length > 0 && (
                                <Typography variant="caption" sx={{ color: colors.danger, display: 'block', wordBreak: 'break-word' }}>
                                  - {obj.facteurs_negatifs.join(', ')}
                                </Typography>
                              )}
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Insights Cles */}
                  {diagnostic.insights?.length > 0 && (
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: dk.text }}>
                        Insights Cles
                      </Typography>
                      <Grid container spacing={1.5}>
                        {diagnostic.insights.slice(0, 5).map((insight, i) => {
                          const insightColor = insight.type === 'positif' ? colors.success :
                            insight.type === 'negatif' ? colors.danger :
                            insight.type === 'anticipation' ? colors.warning : dk.accent;
                          const InsightIcon = insight.type === 'positif' ? CheckCircleOutlineIcon :
                            insight.type === 'negatif' ? ErrorOutlineIcon :
                            insight.type === 'anticipation' ? WarningAmberIcon : AutoAwesomeIcon;

                          return (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                              <Box
                                sx={{
                                  p: 2,
                                  borderRadius: '12px',
                                  border: `1px solid ${insightColor}30`,
                                  bgcolor: `${insightColor}10`,
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                  <InsightIcon sx={{ fontSize: 16, color: insightColor }} />
                                  <Chip
                                    label={insight.priorite || 'moyenne'}
                                    size="small"
                                    sx={{ height: 16, fontSize: '0.68rem', fontWeight: 600, bgcolor: `${insightColor}20`, color: insightColor }}
                                  />
                                </Box>
                                <Typography variant="body2" sx={{ color: dk.textSecondary, flex: 1, lineHeight: 1.5, wordBreak: 'break-word', textAlign: 'justify' }}>
                                  {insight.texte}
                                </Typography>
                                {insight.objectif_lie && (
                                  <Box sx={{ mt: 1, alignSelf: 'flex-start' }}>
                                    {renderMissionBadge(insight.objectif_lie) || (
                                      <Chip
                                        label={insight.objectif_lie}
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 18, fontSize: '0.68rem', borderColor: dk.accent, color: dk.accent }}
                                      />
                                    )}
                                  </Box>
                                )}
                                <Button
                                  size="small"
                                  endIcon={<TrendingUpIcon sx={{ fontSize: 12 }} />}
                                  onClick={() => navigate('/agir')}
                                  sx={{ mt: 1, textTransform: 'none', fontSize: '0.72rem', color: dk.accent, alignSelf: 'flex-start', p: 0, transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline', textUnderlineOffset: '3px' } }}
                                >
                                  Agir
                                </Button>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  )}
                </CollapsibleSection>
              </ScrollReveal>
          )}

          </>)}

            {comprendreTab === 1 && (
              <Box>
                {benchmarkLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={32} sx={{ color: dk.accent }} />
                  </Box>
                ) : !benchmarkData || !benchmarkData.comparison?.length ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.88rem', color: dk.textMuted, fontFamily: '"DM Sans", sans-serif', mb: 2 }}>
                      Ajoutez des concurrents dans Parametres &gt; Concurrents pour activer le benchmark
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/settings')}
                      sx={{
                        borderRadius: '100px', fontFamily: '"DM Sans", sans-serif',
                        fontSize: '0.78rem', color: dk.accent, borderColor: dk.border,
                        textTransform: 'none',
                        '&:hover': { borderColor: dk.accent, bgcolor: dk.accentDim },
                      }}
                    >
                      Configurer les concurrents
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <Typography sx={{
                      fontFamily: '"Playfair Display", serif', fontSize: '1.1rem',
                      fontWeight: 700, color: dk.text, letterSpacing: '0.04em', mb: 1,
                    }}>
                      Comparaison concurrentielle
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.72rem', color: dk.textMuted,
                      fontFamily: '"DM Sans", sans-serif', mb: 4,
                    }}>
                      Donnees mises a jour automatiquement chaque jour
                    </Typography>

                    {/* Entity cards */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
                      {benchmarkData.comparison.map((entity, idx) => (
                        <Box key={idx} sx={{
                          p: 2.5, borderRadius: '14px',
                          bgcolor: dk.surface,
                          border: `1px solid ${dk.border}`,
                          borderLeft: entity.is_brand ? `3px solid ${dk.accent}` : `3px solid ${dk.border}`,
                        }}>
                          {/* Entity header */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <Typography sx={{
                              fontSize: '0.88rem', fontWeight: 700,
                              color: entity.is_brand ? dk.accent : dk.text,
                              fontFamily: '"DM Sans", sans-serif',
                            }}>
                              {entity.name}
                            </Typography>
                            {entity.is_brand && (
                              <Chip label="Votre marque" size="small" sx={{
                                height: 20, fontSize: '0.6rem',
                                bgcolor: dk.accentDim, color: dk.accent,
                                fontFamily: '"DM Sans", sans-serif',
                              }} />
                            )}
                          </Box>

                          {/* KPI row */}
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                            {[
                              { label: 'Score', value: `${entity.score || 0}/100` },
                              { label: 'Sentiment', value: `${entity.sentiment || 0}%` },
                              { label: 'Mentions', value: (entity.mentions_today || 0).toLocaleString() },
                            ].map((kpi) => (
                              <Box key={kpi.label} sx={{ textAlign: 'center' }}>
                                <Typography sx={{
                                  fontSize: '1rem', fontWeight: 700,
                                  color: entity.is_brand ? dk.accent : dk.text,
                                  fontFamily: '"DM Sans", sans-serif',
                                  fontVariantNumeric: 'tabular-nums',
                                }}>
                                  {kpi.value}
                                </Typography>
                                <Typography sx={{
                                  fontSize: '0.62rem', color: dk.textMuted,
                                  fontFamily: '"DM Sans", sans-serif',
                                  textTransform: 'uppercase', letterSpacing: '0.05em',
                                }}>
                                  {kpi.label}
                                </Typography>
                              </Box>
                            ))}
                          </Box>

                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            </Box>

            {/* RIGHT — Photo with citation overlay */}
            <Box
              sx={{
                borderRadius: '20px',
                overflow: 'hidden',
                position: 'relative',
                minHeight: { xs: 300, sm: 420 },
                height: '100%',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {/* Background photo */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage: `url(${brandImageUrl || 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=800&q=80'})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center 20%',
                  filter: 'brightness(0.6) contrast(1.1)',
                }}
              />
              {/* Color overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(135deg, rgba(27,94,64,0.3) 0%, rgba(26,26,24,0.6) 100%)',
                }}
              />
              {/* Text overlay — citation */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 32,
                  left: 32,
                  right: 32,
                  zIndex: 2,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Playfair Display", Georgia, serif',
                    fontStyle: 'italic',
                    fontSize: '1.4rem',
                    color: '#FFFFFF',
                    lineHeight: 1.4,
                    mb: 1.5,
                  }}
                >
                  "{getDailyCitation('comprendre', selectedBrand).text}"
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    color: 'rgba(255,255,255,0.5)',
                    fontFamily: '"DM Sans", sans-serif',
                  }}
                >
                  — {getDailyCitation('comprendre', selectedBrand).author}
                </Typography>
              </Box>
            </Box>
          </Box>
        </>
      )}

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            bgcolor: dk.surface,
            border: `1px solid ${dk.accent}`,
            color: dk.text,
            borderRadius: '12px',
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '0.85rem',
            backdropFilter: 'blur(16px)',
          }
        }}
      />
    </Box>
  );
}

export default ComprendrePage;
